import { type ModalFuncProps } from "antd/lib/modal";

import {
    type IVendorLineItem,
    SendToVendorCartEntityTypes,
    type VendibleLineItem,
    VendorTypes,
} from "entity/vendor/common/vendor.types";

export const getVendorName = (vendorType: VendorTypes) => {
    switch (vendorType) {
        case VendorTypes.TheHomeDepot:
            return "The Home Depot";
        case VendorTypes.Unknown:
            return undefined;
    }
};

export const getVendorIconPath = (vendorType: VendorTypes) => {
    switch (vendorType) {
        case VendorTypes.TheHomeDepot:
            return "/images/Setup/Builder/bsTheHomeDepot.png";
        case VendorTypes.Unknown:
            return undefined;
    }
};

export const getErrorContentFromCode = (
    vendorType: VendorTypes,
    errorCode: string,
): ModalFuncProps => {
    switch (vendorType) {
        case VendorTypes.TheHomeDepot:
            switch (errorCode) {
                case "BT_LINK_ERR_003":
                    return {
                        title: "Must connect to a business account with The Home Depot",
                        content:
                            "The Home Depot company you are trying to connect is not a business account. You will need to update to a business account in your Home Depot Pro Xtra settings to try again.",
                    };
                case "BT_LINK_ERR_004":
                    return {
                        title: "Only one account can be connected",
                        content:
                            "This Home Depot account is already connected to a Buildertrend account. Update your company information in The Home Depot to connect.",
                    };
                case "BT_LINK_ERR_006":
                    return {
                        title: "Company doesn't match",
                        content:
                            "The Home Depot company you are trying to connect does not match the company that is already connected to this Buildertrend account. Update your company information in The Home Depot or disconnect the Buildertrend account to try again.",
                    };
                case "BT_LINK_ERR_007":
                    return {
                        title: "Unable to sync your purchase history",
                        content: (
                            <>
                                <p>
                                    The Home Depot was unable to share your purchased and saved
                                    products with Buildertrend. There may be an issue with your Home
                                    Depot Pro Xtra account.
                                </p>
                                <p>Contact The Home Depot support to resolve.</p>
                            </>
                        ),
                    };
                case "BT_LINK_ERR_008":
                case "BT_LINK_ERR_009":
                    return {
                        title: "Unable to connect",
                        content: (
                            <>
                                <p>
                                    Buildertrend was unable to connect to The Home Depot. Try again
                                    to connect.
                                </p>
                                <p>
                                    If you continue to experience issues, there may be an issue with
                                    your Home Depot Pro Xtra account. Contact The Home Depot support
                                    to resolve.
                                </p>
                            </>
                        ),
                    };
                default:
                    return {
                        title: "Unable to connect",
                        content:
                            "Buildertrend was unable to connect to The Home Depot. Try again to connect.",
                    };
            }
        default:
            return {
                title: "Unable to connect",
                content: "Buildertrend was unable to connect to the vendor. Try again to connect.",
            };
    }
};

export const getVendorProductsAndAmountsFromLineItems = (
    vendorType: VendorTypes,
    lineItems: VendibleLineItem[] | undefined = [],
): IVendorLineItem[] => {
    return (
        lineItems
            ?.filter((li) => li.vendorProductId && li.vendorType === vendorType)
            .map((li) => ({
                vendorProductId: li.vendorProductId!,
                quantity: li.quantity ?? 0,
            })) ?? []
    );
};

export const getVendorProductsFromLineItems = (
    lineItems: VendibleLineItem[] | undefined = [],
): number[] => {
    return lineItems?.filter((li) => li.vendorProductId).map((li) => li.vendorProductId!) ?? [];
};

export const canSendVendorItemsToCart = (
    vendorType: VendorTypes,
    lineItems: VendibleLineItem[] | undefined = [],
) => {
    return (
        lineItems?.some(
            (li) => li.vendorProductId && li.vendorType === vendorType && !li.isDiscontinued,
        ) ?? false
    );
};

export const getSendToCartDisabledText = (
    vendorType: VendorTypes,
    lineItems: VendibleLineItem[] | undefined = [],
): string | undefined => {
    let disabledText;
    switch (vendorType) {
        case VendorTypes.TheHomeDepot:
            disabledText = "Add a Home Depot product from the catalog to send to your cart";
            break;
        case VendorTypes.Unknown:
            return undefined;
    }
    return !canSendVendorItemsToCart(vendorType, lineItems) ? disabledText : undefined;
};

export const getEntityName = (entityType: SendToVendorCartEntityTypes): string => {
    switch (entityType) {
        case SendToVendorCartEntityTypes.Bills:
            return "Bill";
        case SendToVendorCartEntityTypes.ChangeOrders:
            return "Change Order";
        case SendToVendorCartEntityTypes.Estimates:
            return "Estimate";
        case SendToVendorCartEntityTypes.LeadProposals:
            return "Lead Proposal";
        case SendToVendorCartEntityTypes.PurchaseOrders:
            return "Purchase Order";
        case SendToVendorCartEntityTypes.SelectionChoices:
            return "Selection Choice";
    }
};

/**
 * Will update the properties related to vendor product info in the case
 * that a link was broken on the client side. The result should be merged
 * with any existing data for that line item.
 * @param lineItem Line item that potentially had link broken
 * @param isLinkBroken Was the line item link broken
 */
export const clearVendorProductInfoOnBrokenLinks = (
    lineItem: VendibleLineItem,
    isLinkBroken: boolean = false,
) => {
    if (isLinkBroken) {
        return {
            vendorProductId: undefined,
            vendorType: undefined,
            isDiscontinued: false,
        } as Partial<VendibleLineItem>;
    }
    return {
        vendorProductId: lineItem.vendorProductId,
        vendorType: lineItem.vendorType,
        isDiscontinued: lineItem.isDiscontinued,
    } as Partial<VendibleLineItem>;
};
